/**
 * Methods for tracking events on the client. Currently tracks to Mixpanel.
 *
 * Mixpanel is tracked on the server-side using the growth pixel.
 *
 * Copyright (C) 2018 Noom, Inc.
 * @author sumin, patrick
 */
import Levenshtein from "levenshtein";

import { logInfo } from "../monitoring/logging";
import { emitMonitoringEvent } from "../monitoring/bus";
import { CoreReduxState } from "../redux/internal";
import {
  WellknownMixpanelEvents,
  NoomGrowthEvents,
} from "../monitoring/events";

export function updateUserContext(properties: JsonObject) {
  logInfo("EventTracker", "updateUserContext", properties);
  emitMonitoringEvent({
    type: "people_set",
    properties,
  });
}

/**
 * Track a mixpanel event.
 */
export function trackEvent<K extends keyof WellknownMixpanelEvents>(
  eventName: K,
  properties: WellknownMixpanelEvents[K]
);
export function trackEvent(eventName: string, properties?: JsonObject);
export function trackEvent(eventName: string, properties: JsonObject = {}) {
  logInfo("EventTracker", "trackEvent", eventName, properties);
  return emitMonitoringEvent({
    type: "track",
    eventName,
    properties,
  });
}

export function trackDataStreamEvent<K extends keyof NoomGrowthEvents>(
  eventName: K,
  properties: NoomGrowthEvents[K]
) {
  logInfo(
    "DataStreamEventTracker",
    "trackDataStreamEvent",
    eventName,
    properties
  );
  return emitMonitoringEvent({
    type: "dataStreamEvent",
    eventName,
    properties: properties as JsonObject,
  });
}

export function trackGotEmail(email: string) {
  // TODO(sumin): Remove this since we add email and name to people properties on the server-side.
  trackEvent("GotEmail", {
    EnteredEmail: email,
  });
}

export function trackEmailChanged(
  location: string,
  email: string,
  state: CoreReduxState
) {
  const { userData, winbackInfo } = state;

  if (userData.email && userData.email !== email) {
    const [oldUserName, oldDomain] = userData.email.split("@");
    const [newUserName, newDomain] = email.split("@");

    trackEvent("UserEmailChanged", {
      location,
      oldEmail: userData.email,
      newEmail: email,

      oldDomain,
      newDomain,

      userNameChanged:
        oldUserName?.toLowerCase() !== newUserName?.toLowerCase(),
      domainChanged: oldDomain?.toLowerCase() !== newDomain?.toLowerCase(),
      levensteinDistance: new Levenshtein(oldUserName, newUserName).distance,

      oldWinbackStatus: winbackInfo.previousWinbackStatus,
      newWinbackStatus: winbackInfo.winbackStatus,
      mainSurveyCompleteDateISO: userData.mainSurveyCompleteDateISO,
      hasAccessCode: !!userData.access_code,
    });
  }
}
